/**
 * b27 레인 A — 단지백과 카드 «부가 데이터 칸» (순수 함수 · 잠금: src/__tests__/b27-lane-a.test.ts).
 *
 * R1: 값이 없는 칸은 「—」 자리표시 대신 «칸째» 사라진다.
 *   종전: 전세·갭투자 값이 없는 카드 ~88장이 「전세 —」「갭투자 —」 를 그대로 보여 줬다(나머지 칸도 같은 꼴).
 * 순서는 종전 그대로(전세 · 평당가 · 갭투자 · 매매 · 전월세 · 월세) — 빈 칸만 건너뛴다. 배치는 바꾸지 않는다.
 */
import { fmtAmount } from '@/lib/format';
import { fmtCnt } from '@/lib/apt-format';

export type ComplexCardInput = {
  lastPrice?: number | null;
  jeonse?: number | null;
  pyeongPrice?: number | null;
  saleCount?: number | null;
  rentCount?: number | null;
  monthlyRent?: number | null;
};
export type ComplexCardCell = { label: string; value: string; color: string };

const pos = (n: unknown): n is number => typeof n === 'number' && Number.isFinite(n) && n > 0;

export function complexCardCells(c: ComplexCardInput): ComplexCardCell[] {
  const cells: (ComplexCardCell | null)[] = [
    pos(c.jeonse) ? { label: '전세', value: fmtAmount(c.jeonse), color: 'var(--accent-green)' } : null,
    pos(c.pyeongPrice) ? { label: '평당가', value: fmtAmount(c.pyeongPrice), color: 'var(--accent-purple)' } : null,
    // 갭 = 매매 − 전세. 둘 중 하나라도 없거나 차이가 0 이하(전세 ≥ 매매)면 칸을 내지 않는다.
    pos(c.lastPrice) && pos(c.jeonse) && c.lastPrice - c.jeonse > 0
      ? { label: '갭투자', value: fmtAmount(c.lastPrice - c.jeonse), color: 'var(--accent-orange)' } : null,
    pos(c.saleCount) ? { label: '매매', value: `${fmtCnt(c.saleCount)}건`, color: 'var(--brand)' } : null,
    pos(c.rentCount) ? { label: '전월세', value: `${fmtCnt(c.rentCount)}건`, color: 'var(--accent-cyan)' } : null,
    pos(c.monthlyRent) ? { label: '월세', value: `${c.monthlyRent}만`, color: 'var(--text-secondary)' } : null,
  ];
  return cells.filter((x): x is ComplexCardCell => x !== null);
}

/**
 * 3열 격자의 마지막 줄을 채울 «빈 채움 칸» 수(글자 없음 · 배경만).
 * 격자가 gap 1px + 테두리색 배경으로 칸막이를 그리기 때문에, 칸이 모자라면 그 자리가 회색 덩어리로 보인다.
 */
export function complexCardFillers(cellCount: number, cols = 3): number {
  if (cellCount <= 0) return 0;
  const used = Math.min(cellCount, cols) === cellCount ? 0 : cellCount % cols;
  return used === 0 ? 0 : cols - used;
}

/** 격자 열 수 — 칸이 3개 미만이면 그만큼만(1칸짜리가 1/3 폭으로 쪼그라들지 않게). */
export function complexCardCols(cellCount: number): number {
  return Math.max(1, Math.min(cellCount, 3));
}
